'use client';

import React, { useEffect } from 'react';
import { useTrack } from '@chewy/use-analytics';
import { navigateToAnchor } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';
import LinksPanel, { LinkData } from '@/components/helpCenter/common/linksPanel';
import isEmpty from 'lodash/isEmpty';

export interface RelatedArticlesPanelProps {
  readonly title?: string | null;
  readonly linksData?: LinkData[] | null;
}

const RelatedArticlesPanel: React.FC<RelatedArticlesPanelProps> = ({ title, linksData = [] }) => {
  const router = useRouter();
  const track = useTrack();

  const hasTrackedView = React.useRef(false);

  useEffect(() => {
    if (isEmpty(linksData) || hasTrackedView.current) {
      return;
    }

    hasTrackedView.current = true;

    track('Related Articles Panel Viewed', {
      id: 'related-articles-panel-viewed-id',
      parentId: 'related-articles-panel-id',
      level: 'related-articles-panel',
      type: 'static',
      position: 1,
      event_name: 'Widget Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-article',
      eventLabel: 'help-center-article-related-articles-view',
      name: 'Related Articles Panel Viewed'
    });
  }, [linksData, track]);

  const handleClick = (linkData: any) => {
    track('Related Articles Panel Link Clicked', {
      id: 'related-articles-panel-link-clicked-id',
      parentId: 'related-articles-panel-id',
      level: 'related-articles-panel',
      type: 'static',
      position: 1,
      event_name: 'Link Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-article',
      eventLabel: 'help-center-article-related-articles-click',
      name: 'Related Articles Panel Link Clicked',
      articleId: linkData?.id,
      articleTitle: linkData?.title,
      articleCategoryId: linkData?.categoryId,
      articleSectionId: linkData?.sectionId,
      articleSectionTitle: linkData?.sectionTitle,
      articleSectionCategoryId: linkData?.sectionCategoryId
    });

    navigateToAnchor(linkData?.id, router);
  };

  return !isEmpty(linksData) ? <LinksPanel linksData={linksData} title={title} handleClick={handleClick} /> : null;
};

export default RelatedArticlesPanel;
